import React, { useEffect, useState, useCallback } from "react";
import {
    Button, Grid, Typography, TextField, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip, Box
} from "@mui/material";
import {
    Search as IconSearch, AddCircle as IconAddCircle,
    CheckCircle as IconCheckCircle, Block as IconBlock
} from "@mui/icons-material";
import { useNavigate, Link } from "react-router-dom";

import * as cargoService from "../../services/cargoService";
import DataGridBase from "../../components/common/DataGridBase";
import { useMantenimiento } from "../../hooks/useMantenimiento";
import GrupoCargoAutocomplete from "../../components/common/GrupoCargoAutocomplete";
import DialogYesNo from "../../components/DialogYesNo";

/**
 * Pantalla de navegabilidad para Cargo.
 * Sigue la plantilla estándar del sistema:
 * - Filtros superiores de búsqueda (incluyendo filtro por Grupo de Cargo).
 * - DataGridBase con barra de herramientas integrada (botón Nuevo, botón del ojo para auditoría, paginación arriba y abajo).
 * - Enlace en el código hacia la pantalla de mantenimiento /cargo/edit/:id.
 */
const Index = () => {
    const { isReadOnly } = useMantenimiento("trabajador");
    const navigate = useNavigate();

    const [filters, setFilters] = useState({ descripcion: "", idEstado: 0, idGrupoCargo: 0 });
    const [results, setResults] = useState([]);
    const [cargando, setCargando] = useState(true);

    const [dialogAnnul, setDialogAnnul] = useState({ open: false, id: 0, estado: "" });

    const fetchResults = useCallback(async () => {
        setCargando(true);
        try {
            const data = await cargoService.getsCargoViewSearchBasic(
                filters.descripcion,
                filters.idEstado,
                filters.idGrupoCargo
            );
            const items = (data || []).map((row) => ({
                ...row,
                id: row.id || row.idCargo
            }));
            setResults(items);
        } catch (err) {
            console.error("Error fetching cargos:", err);
            setResults([]);
        }
        setCargando(false);
    }, [filters]);

    useEffect(() => {
        fetchResults();
    }, [fetchResults]);

    const toggleEstado = async () => {
        const { id, estado } = dialogAnnul;
        if (!id) return;

        try {
            if (estado === "Activo") {
                await cargoService.annulCargo(id);
            } else {
                await cargoService.activeCargo(id);
            }
            fetchResults();
        } catch (err) {
            console.error("Error al cambiar estado:", err);
        }
        setDialogAnnul({ open: false, id: 0, estado: "" });
    };

    const columns = [
        {
            field: "id",
            headerName: "ID",
            width: 60,
            minWidth: 50,
            sortable: true,
            headerAlign: "center",
            align: "center",
            renderCell: (p) => (
                <span style={{ fontSize: "0.75rem", color: "#666", fontFamily: "monospace" }}>
                    {p.value != null ? p.value : p.row.id}
                </span>
            )
        },
        {
            field: "codigo",
            headerName: "Código",
            width: 120,
            sortable: true,
            headerAlign: "left",
            renderCell: (cellValues) => (
                <Link to={"/cargo/edit/" + cellValues.row.id} style={{ color: "inherit", fontWeight: 500 }}>
                    {cellValues.row.codigo}
                </Link>
            )
        },
        { field: "descripcion", headerName: "Descripción", width: 280, sortable: true, headerAlign: "left" },
        { field: "grupoCargo", headerName: "Grupo de Cargo", width: 220, sortable: true, headerAlign: "left" },
        { field: "estado", headerName: "Estado", width: 100, sortable: true, headerAlign: "center", align: "center" },
        {
            field: "acciones",
            headerName: "Acciones",
            width: 90,
            sortable: false,
            headerAlign: "center",
            align: "center",
            renderCell: (c) => {
                const esActivo = c.row.estado === "Activo" || c.row.idEstado === 2;
                return (
                    <Tooltip title={esActivo ? "Anular Cargo" : "Activar Cargo"}>
                        <span>
                            <IconButton
                                size="small"
                                disabled={isReadOnly}
                                color={esActivo ? "warning" : "success"}
                                onClick={() => setDialogAnnul({
                                    open: true,
                                    id: c.row.id || c.row.idCargo,
                                    estado: c.row.estado
                                })}
                            >
                                {esActivo ? <IconBlock fontSize="small" /> : <IconCheckCircle fontSize="small" />}
                            </IconButton>
                        </span>
                    </Tooltip>
                );
            }
        }
    ];

    return (
        <div>
            <Grid container>
                <Grid item xs={12}>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        Cargos
                    </Typography>
                </Grid>

                {/* Barra de Filtros */}
                <Grid container spacing={1.5} alignItems="center" sx={{ mt: 1, mb: 2 }}>
                    <Grid item xs={12} sm={4} md={3.5}>
                        <GrupoCargoAutocomplete
                            value={filters.idGrupoCargo}
                            onChange={(id) => setFilters({ ...filters, idGrupoCargo: id })}
                            soloActivos={false}
                            label="Filtrar Grupo Cargo"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} md={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Buscar por Código o Descripción"
                            value={filters.descripcion}
                            onChange={(e) => setFilters({ ...filters, descripcion: e.target.value })}
                            onKeyDown={(e) => { if (e.key === "Enter") fetchResults(); }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={2} md={2.5}>
                        <FormControl fullWidth size="small">
                            <InputLabel shrink>Estado</InputLabel>
                            <Select
                                value={filters.idEstado}
                                label="Estado"
                                onChange={(e) => setFilters({ ...filters, idEstado: Number(e.target.value) })}
                            >
                                <MenuItem value={0}>(Todos)</MenuItem>
                                <MenuItem value={2}>Activo</MenuItem>
                                <MenuItem value={1}>Anulado</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={2} md={2}>
                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<IconSearch />}
                            onClick={fetchResults}
                        >
                            Buscar
                        </Button>
                    </Grid>
                </Grid>

                {/* Listado con DataGridBase estándar */}
                <Grid item xs={12}>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr" }}>
                        <DataGridBase
                            rows={results}
                            columns={columns}
                            loading={cargando}
                            disabled={isReadOnly}
                            pageSize={10}
                            pageSizeOptions={[10, 20, 50, 100]}
                            toolbarButtons={[
                                {
                                    text: "Nuevo",
                                    icon: <IconAddCircle />,
                                    disabled: isReadOnly,
                                    onClick: () => navigate("/cargo/edit/0")
                                }
                            ]}
                        />
                    </Box>
                </Grid>
            </Grid>

            {/* Dialogo Confirmacion Activar/Anular */}
            <DialogYesNo
                open={dialogAnnul.open}
                openClose={() => setDialogAnnul({ open: false, id: 0, estado: "" })}
                setYes={toggleEstado}
                titulo={dialogAnnul.estado === "Activo" ? "Anular Cargo" : "Activar Cargo"}
                texto={`¿Está seguro de ${dialogAnnul.estado === "Activo" ? "anular" : "activar"} este cargo?`}
            />
        </div>
    );
};

export default Index;
